import { ldb, emitter } from '@codesuma/baseline'
import starfield from './starfield'

// Light/dark theme, switchable from the bottom-of-page toggle next to the font
// picker. The choice is written as `data-theme` on <html> (CSS variables and
// `[data-theme="dark"]` rules cascade from there), persisted in localStorage, and
// re-applied on boot before first paint. Until the user picks one, we follow the
// OS preference. A `theme` event is emitted on change so JS-colored bits (the list
// circles, the open list page) can recolor without a full rebuild.
export const THEMES = ['light', 'dark'] as const
export type Theme = typeof THEMES[number]

const KEY = 'app-theme'

const systemPref = (): Theme =>
    window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

const get = (): Theme => {
    const saved = ldb.get(KEY)
    return saved === 'light' || saved === 'dark' ? saved : systemPref()
}

const apply = (theme: Theme): void => {
    document.documentElement.setAttribute('data-theme', theme)

    // Browser chrome / native status bar follow the theme.
    document.querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', theme === 'dark' ? '#141d4a' : '#efecf3')
    const cap = (window as any).Capacitor
    if (cap?.isNativePlatform?.()) {
        // Capacitor StatusBar: Style.Dark = light content (for a dark bg), Style.Light = dark content.
        cap.Plugins?.StatusBar?.setStyle({ style: theme === 'dark' ? 'DARK' : 'LIGHT' })
    }

    if (theme === 'dark') starfield.show()
    else starfield.hide()
}

const set = (theme: Theme): void => {
    ldb.set(KEY, theme)
    apply(theme)
    emitter.emit('theme', theme)
}

const toggle = (): void => set(get() === 'dark' ? 'light' : 'dark')

const init = (): void => apply(get())

export default { THEMES, get, set, toggle, init }
